import type { UnidadProyecto } from "./api";
import { TOKEN_ESTADO } from "./maqueta";
import { etiquetaPiso, formatM2, nombrePiso } from "./unidades";

/**
 * El frente del edificio en Proyecto (como baseproyectos.com; Mati, 2026-10-31): un botón por piso con una ventana por
 * unidad. Se arma solo con las unidades cargadas: no hace falta subir dibujos.
 * Los m² se usan sólo para el ancho de cada caja (proporción), nunca para cuentas.
 */

type Unidad = Pick<UnidadProyecto, "id" | "numero" | "piso" | "tipo" | "sup_total_m2" | "ambientes" | "orientacion" | "estado_venta">;

/** Lo mínimo de ancho de una unidad (en m²) para que entren su número y su detalle. */
const PESO_MINIMO = 30;

export interface VentanaFachada {
  id: number;
  token: string;
  peso: number;
}

/**
 * Un piso que es parte de la unidad del de abajo, sin unidades propias (Newbery 3563: la azotea del 8 es la terraza del
 * dúplex del 7). En el frente va junto con ese piso.
 */
export interface PisoAnexo {
  piso: number;
  de: number;
}

export interface PisoFachada {
  piso: number;
  /** De arriba hacia abajo: la del piso y, encima, las de sus pisos anexos ("8°", "7°"). */
  etiquetas: string[];
  nombre: string;
  total: number;
  disponibles: number;
  resumen: string;
  ventanas: VentanaFachada[];
}

function peso(u: Unidad): number {
  return Math.max(PESO_MINIMO, Number(u.sup_total_m2) || 0);
}

function porNumero(a: Unidad, b: Unidad): number {
  return a.numero.localeCompare(b.numero, "es", { numeric: true });
}

function resumen(total: number, disponibles: number): string {
  if (total === 0) return "Sin unidades";
  const unidades = `${total} ${total === 1 ? "unidad" : "unidades"}`;
  const libres = disponibles === 0 ? "ninguna disponible" : `${disponibles} ${disponibles === 1 ? "disponible" : "disponibles"}`;
  return `${unidades} · ${libres}`;
}

/** "Piso 7", "Pisos 7 y 8". */
function nombreConAnexos(piso: number, anexos: number[]): string {
  if (anexos.length === 0) return nombrePiso(piso);
  const todos = [piso, ...anexos].sort((a, b) => a - b);
  return `Pisos ${todos.slice(0, -1).join(", ")} y ${todos.at(-1)}`;
}

/**
 * Los pisos del frente, de arriba hacia abajo: del más alto al subsuelo más profundo (o la PB), con los del medio aunque
 * no tengan unidades. Los pisos anexos no van solos: se suman al piso del que son parte (sus unidades se filtran antes).
 */
export function pisosFachada(unidades: Unidad[], anexos: PisoAnexo[] = []): PisoFachada[] {
  if (unidades.length === 0) return [];
  const pisos = unidades.map((u) => u.piso);
  const alto = Math.max(...pisos, ...anexos.map((a) => a.piso));
  const bajo = Math.min(0, ...pisos);
  const fila: PisoFachada[] = [];
  for (let piso = alto; piso >= bajo; piso--) {
    if (anexos.some((a) => a.piso === piso)) continue;
    const suyos = anexos.filter((a) => a.de === piso).map((a) => a.piso);
    const suyas = unidades.filter((u) => u.piso === piso).sort(porNumero);
    const disponibles = suyas.filter((u) => u.estado_venta === "disponible").length;
    fila.push({
      piso,
      etiquetas: [...suyos.sort((a, b) => b - a), piso].map(etiquetaPiso),
      nombre: nombreConAnexos(piso, suyos),
      total: suyas.length,
      disponibles,
      resumen: resumen(suyas.length, disponibles),
      ventanas: suyas.map((u) => ({ id: u.id, token: TOKEN_ESTADO[u.estado_venta], peso: peso(u) })),
    });
  }
  return fila;
}

/** "3 amb. · 72,5 m²", "Monoambiente · 35 m²", "12 m²" (sin ambientes cargados). */
export function detalleCelda(u: Pick<Unidad, "ambientes" | "sup_total_m2">): string {
  const m2 = formatM2(u.sup_total_m2);
  if (u.ambientes === null || u.ambientes === 0) return m2;
  return `${u.ambientes === 1 ? "Monoambiente" : `${u.ambientes} amb.`} · ${m2}`;
}
